import {
  Box,
  Divider,
  FormControl,
  Grid,
  Input,
  Modal,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../api/queryKeys";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import {
  MSSP_PLATFORM_ACCESS_ROLES,
  UserPlatformAccessRole,
} from "../../../utils/constants";
import ChangeAccessRoleDialog from "./ChangeAccessRoleDialog";
import OwnershipsSelect from "./OwnershipsSelect";
import RoleSelection from "./RoleSelection";
import TitleTextAndSelection from "./TitleTextAndSelection";
import { getDisplayEmail, handleKeyDownForTextInput, isAdmin } from "./utils";

const EditPlatformUser = ({
  open,
  handleClose,
  platformUser,
  titleOptions,
}) => {
  const colors = useColors();
  const { updatePlatformUser, trackEvent } = useAPI();
  const queryClient = useQueryClient();

  const [userDetails, setUserDetails] = useState({
    title: platformUser?.role || "",
    accessRole: platformUser?.access_role || "",
    name: platformUser?.display_name || "",
    ownerships: platformUser?.ownerships || [],
  });

  useEffect(() => {
    setUserDetails({
      title: platformUser?.role || "",
      accessRole: platformUser?.access_role || "",
      name: platformUser?.display_name || "",
      ownerships: platformUser?.ownerships || [],
    });
  }, [platformUser]);

  const [onSave, setOnSave] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState("");

  const resetAllProperties = () => {
    setUserDetails({
      title: platformUser?.role || "",
      accessRole: platformUser?.access_role || "",
      name: platformUser?.display_name || "",
      ownerships: platformUser?.ownerships || [],
    });
  };

  const handleSave = () => {
    setOnSave(true);
    const isUserAdmin = isAdmin(userDetails.accessRole);
    updatePlatformUser(
      platformUser.email,
      userDetails.name,
      userDetails.title,
      userDetails.ownerships,
      isUserAdmin,
      userDetails.accessRole
    )
      .then((response) => {
        trackEvent(`User edit ${platformUser.email} details`);
        Toast.success("User details updated successfully");
        queryClient.invalidateQueries({
          queryKey: platformUsersKeys.all,
        });
        handleCancel();
      })
      .catch((error) => {
        trackEvent(`User failed to edit ${platformUser.email} details`);
        if (error.response) {
          Toast.error(error.response.data);
        } else {
          Toast.error("Failed to edit user details");
        }
      })
      .finally(() => {
        setOnSave(false);
      });
  };

  const handleCancel = () => {
    trackEvent("User cancelled the edit user dialog");
    resetAllProperties();
    handleClose();
  };

  const handleTitleTypeSelection = (titleType) => {
    trackEvent(`User selected title type ${titleType}`);
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      title: titleType,
    }));
  };

  const handleAccessRoleSelection = (accessRole) => {
    setPendingRole(accessRole);
    setIsDialogOpen(true);
  };

  const handleConfirmRoleChange = () => {
    trackEvent(`User confirmed role change to ${pendingRole}`);
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      accessRole: pendingRole,
    }));
    setIsDialogOpen(false);
    setPendingRole("");
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setPendingRole("");
  };

  const allFieldsFilled =
    userDetails.title?.length > 0 &&
    userDetails.accessRole?.length > 0 &&
    userDetails.name?.length > 0;

  const someFieldsChanged =
    userDetails.title !== platformUser.role ||
    userDetails.accessRole !== platformUser.access_role ||
    userDetails.name !== platformUser.display_name ||
    userDetails.ownerships !== platformUser.ownerships;

  const isFormValidAndChanged = allFieldsFilled && someFieldsChanged;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
      >
        <Box
          sx={{
            width: 500,
            bgcolor: "background.paper",
            margin: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            overflowY: "auto",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: colors.common_white,
                pt: "5%",
              }}
              id="modal-modal-title"
            >
              <Typography variant="subtitle1" gutterBottom>
                Edit User
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ marginTop: "4px" }}
              >
                Edit user details
              </Typography>
            </Box>
            <Box sx={{ padding: "32px" }}>
              {/* email */}
              <FormControl variant="standard" fullWidth>
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Email
                </Typography>
                <Input
                  placeholder="Enter email"
                  value={getDisplayEmail(platformUser)}
                  readOnly
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${colors.grey_32percent}`,
                      fontSize: "12px",
                    },
                  }}
                />
              </FormControl>
              <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />
              {/* name */}
              <FormControl variant="standard" fullWidth>
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Full Name
                </Typography>
                <Input
                  placeholder="Enter name"
                  value={userDetails.name}
                  onKeyDown={(event) => handleKeyDownForTextInput(event)}
                  onChange={(event) =>
                    setUserDetails((prevDetails) => ({
                      ...prevDetails,
                      name: event.target.value,
                    }))
                  }
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${colors.grey_32percent}`,
                      fontSize: "12px",
                      color: colors.common_black,
                    },
                  }}
                />
              </FormControl>
              <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

              {/* title  */}
              <FormControl variant="standard" fullWidth>
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Title
                </Typography>

                <TitleTextAndSelection
                  selection={userDetails.title}
                  placeholder="Select user title"
                  setSelection={handleTitleTypeSelection}
                  selectionList={titleOptions}
                />
              </FormControl>
              {/* access role */}
              <FormControl
                variant="standard"
                fullWidth
                sx={{ marginTop: "16px" }}
              >
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Role
                </Typography>

                <RoleSelection
                  selection={userDetails.accessRole}
                  placeholder="Select role type"
                  setSelection={handleAccessRoleSelection}
                  selectionList={Object.values(UserPlatformAccessRole).filter(
                    (role) => !MSSP_PLATFORM_ACCESS_ROLES.includes(role)
                  )}
                />
              </FormControl>

              {/* ownership */}
              <FormControl
                variant="standard"
                fullWidth
                sx={{ marginTop: "16px" }}
              >
                <Grid container direction="column" gap={0.5}>
                  <Typography variant="subtitle2" fontSize={"14px"}>
                    Ownerships
                  </Typography>
                  <OwnershipsSelect
                    userDetails={userDetails}
                    setUserDetails={setUserDetails}
                  />
                </Grid>
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <SecondaryButton
                onClick={handleCancel}
                sx={{
                  color: colors.primary_main,
                  borderRadius: "10px",
                  border: "none",
                  height: "30px",
                  fontSize: "12px",
                }}
              >
                Cancel
              </SecondaryButton>
              {onSave ? (
                <LoadingIcon />
              ) : (
                <PrimaryButton
                  onClick={handleSave}
                  disabled={!isFormValidAndChanged || onSave}
                  size="small"
                >
                  Save
                </PrimaryButton>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
      {/* Role Change Dialog */}
      <ChangeAccessRoleDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={handleDialogClose}
        handleConfirmRoleChange={handleConfirmRoleChange}
        role={pendingRole}
      />
    </>
  );
};

export default EditPlatformUser;
